import React from "react";
import { Box, Chip, Link, Stack, Typography } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import type { BotNote } from "features/adminUsers/adminUsers";

/** "edited by kiril.gogol on 10/19/2026" - a note is somebody's word, and the
 *  operator reading it is owed whose and how old. */
/** Only http(s) is rendered as a link: anything else would run whatever
 *  scheme it names in the operator's own session. */
function isWebAddress(url: string | undefined): boolean {
  return !!url && (url.startsWith("https://") || url.startsWith("http://"));
}

function byline(note: BotNote): string {
  const when = note.updated_at
    ? new Date(note.updated_at).toLocaleDateString()
    : "";
  const who = note.updated_by || "";
  if (who && when) return `${who}, ${when}`;
  return who || when;
}

/**
 * What a DPM needs to know before running this vendor's bots, or this bot
 * (CAL-6162).
 *
 * A note is prose plus the pages it points at. A Confluence link is kept by
 * title rather than by URL - that is all the page's storage format holds -
 * so a link with no URL is shown as the title to search for.
 */
export default function BotNoteView({
  note,
  label,
  dense = false,
}: {
  note: BotNote | null | undefined;
  /** Whose note this is: "Paylocity" or "download_bots.paystubs". */
  label?: string;
  dense?: boolean;
}) {
  if (!note || (!note.text && !(note.links || []).length)) return null;
  return (
    <Box
      sx={{
        p: dense ? 1 : 1.25,
        borderRadius: 1,
        bgcolor: "action.hover",
        border: 1,
        borderColor: "divider",
        minWidth: 0,
      }}
    >
      <Stack direction="row" spacing={0.75} alignItems="flex-start" sx={{ minWidth: 0 }}>
        <InfoOutlinedIcon fontSize="small" sx={{ color: "text.disabled", mt: "2px" }} />
        <Box sx={{ minWidth: 0 }}>
          {label ? (
            <Typography
              variant="caption"
              sx={{ color: "text.secondary", fontWeight: 700, display: "block" }}
            >
              {label}
            </Typography>
          ) : null}
          {note.text ? (
            <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>
              {note.text}
            </Typography>
          ) : null}
          {(note.links || []).length ? (
            <Stack direction="row" sx={{ flexWrap: "wrap", gap: 0.5, mt: 0.5 }}>
              {note.links.map((link, index) =>
                isWebAddress(link.url) ? (
                  <Link
                    key={`${link.url}-${index}`}
                    href={link.url}
                    target="_blank"
                    rel="noreferrer"
                    variant="caption"
                  >
                    {link.label || link.url}
                  </Link>
                ) : (
                  <Chip
                    key={`${link.label}-${index}`}
                    size="small"
                    variant="outlined"
                    label={link.label || link.url}
                    title="A Confluence page - search for it by this title"
                    sx={{ height: 20, fontSize: "0.68rem" }}
                  />
                ),
              )}
            </Stack>
          ) : null}
          {byline(note) ? (
            <Typography variant="caption" sx={{ color: "text.disabled", display: "block", mt: 0.25 }}>
              {byline(note)}
            </Typography>
          ) : null}
        </Box>
      </Stack>
    </Box>
  );
}
